import type { Period } from "./types";

export const OBSERVABILITY_SCHEMA_QUERY = `
  query ObservabilityContractCheck {
    __type(name: "RootQueryType") {
      fields {
        name
      }
    }
  }
`;

/**
 * The event FEED — rows for the table, fetched a page at a time.
 *
 * `limit` is capped server-side at 500 (`ObservabilityContext.@max_limit`), which
 * is why this is paged rather than asked for in one shot. Nothing that adds up
 * money may be derived from it: a single page silently under-reported every org
 * past the cap (dev 2026-09-21, org 59b63e2f: 1942 events, tab showed $2.096740
 * against the $2.816774 the SQL aggregate reports for the same window). Totals
 * come from OBSERVABILITY_SUMMARY_QUERY below.
 */
export const OBSERVABILITY_QUERY = `
  query ObservabilityEvents($organizationId: ID!, $since: DateTime, $limit: Int, $offset: Int) {
    observabilityEvents(organizationId: $organizationId, since: $since, limit: $limit, offset: $offset) {
      id
      traceId
      organizationId
      userId
      service
      surface
      action
      runId
      status
      inputTokens
      outputTokens
      costUsd
      latencyMs
      metadata
      error
      occurredAt
    }
  }
`;

/**
 * The TOTALS — one uncapped SQL aggregate over the whole window
 * (`ObservabilityContext.summary/3`), not a sum over whatever rows we happened
 * to fetch. Same emitter table as the feed above, same `user_id IS NOT NULL`
 * rule the feed's spend filter applies, so the two agree by construction.
 */
export const OBSERVABILITY_SUMMARY_QUERY = `
  query ObservabilitySummary($organizationId: ID!, $since: DateTime) {
    observabilitySummary(organizationId: $organizationId, since: $since) {
      eventCount
      runCount
      failedCount
      inputTokens
      outputTokens
      costUsd
      avgLatencyMs
      byService {
        service
        eventCount
        runCount
        failedCount
        inputTokens
        outputTokens
        costUsd
        avgLatencyMs
      }
    }
  }
`;

/** Server page size. The backend clamps anything larger to 500. */
export const EVENTS_PAGE_SIZE = 500;

/**
 * How many pages the feed will walk at most. A bound, not a target — it stops as
 * soon as a short page says the window is exhausted. Without one, a busy org
 * with a 90d window could spin for dozens of round trips on a screen nobody is
 * waiting on that long for.
 */
export const EVENTS_MAX_PAGES = 10;

export function sinceForPeriod(period: Period): string {
  const days = period === "24h" ? 1 : period === "7d" ? 7 : period === "30d" ? 30 : 90;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}
